<script lang="ts">
  import Check from '@lucide/svelte/icons/check';

  import type { Id, Rating } from '../model/types';
  import { readableOn } from './color';

  interface Props {
    ratings: readonly Rating[];
    /** Selected rating id. */
    value: Id;
    /** Group label, e.g. "Spanking, Giving". */
    label: string;
    /** Role name appended to each option's name ("Favorite — Giving") when a kink has several roles. */
    roleName?: string;
    onchange: (ratingId: Id) => void;
  }

  let { ratings, value, label, roleName, onchange }: Props = $props();

  let group: HTMLElement | undefined = $state();
  const selectedIndex = $derived(
    Math.max(
      0,
      ratings.findIndex((r) => r.id === value),
    ),
  );
  // The first rating means "not rated yet": when selected it is a plain larger ring
  // so actual choices stand out; real ratings get a check mark.
  const unsetSelected = $derived(selectedIndex === 0);

  function choose(index: number) {
    const rating = ratings[index];
    if (!rating) return;
    if (rating.id !== value) onchange(rating.id);
    group?.querySelectorAll<HTMLElement>('[role="radio"]')[index]?.focus();
  }

  function onkeydown(event: KeyboardEvent) {
    const count = ratings.length;
    const moves: Record<string, number> = {
      ArrowRight: selectedIndex + 1,
      ArrowDown: selectedIndex + 1,
      ArrowLeft: selectedIndex - 1,
      ArrowUp: selectedIndex - 1,
      Home: 0,
      End: count - 1,
    };
    let target = moves[event.key];
    if (
      target === undefined &&
      /^[1-9]$/.test(event.key) &&
      !event.altKey &&
      !event.ctrlKey &&
      !event.metaKey
    ) {
      const n = Number(event.key) - 1;
      if (n < count) target = n;
    }
    if (target === undefined) return;
    event.preventDefault();
    choose((target + count) % count);
  }
</script>

<div
  bind:this={group}
  role="radiogroup"
  aria-label={label}
  tabindex="-1"
  class="flex flex-wrap items-center"
  {onkeydown}
>
  {#each ratings as rating, i (rating.id)}
    {@const selected = i === selectedIndex}
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={roleName ? `${rating.name} — ${roleName}` : rating.name}
      tabindex={selected ? 0 : -1}
      class="group/swatch relative grid size-6 shrink-0 place-items-center squircle outline-offset-0 pointer-coarse:size-11"
      onclick={() => {
        choose(i);
      }}
    >
      <span
        class={[
          'grid place-items-center squircle border border-line transition-transform duration-150',
          selected && !unsetSelected && 'size-6 ring-2 ring-ink pointer-coarse:size-8',
          selected && unsetSelected && 'size-6 pointer-coarse:size-8',
          !selected && 'size-5 group-hover/swatch:scale-110 pointer-coarse:size-7',
        ]}
        style:background-color={rating.color}
      >
        {#if selected && !unsetSelected}
          <Check size={14} strokeWidth={3} color={readableOn(rating.color)} aria-hidden="true" />
        {/if}
      </span>
      <span
        aria-hidden="true"
        class="pointer-events-none absolute bottom-full left-1/2 z-20 mb-1 -translate-x-1/2 rounded-md bg-ink px-2 py-0.5 text-xs font-medium whitespace-nowrap text-canvas opacity-0 transition-opacity delay-300 duration-150 group-hover/swatch:opacity-100 pointer-coarse:hidden"
      >
        {rating.name}
      </span>
    </button>
  {/each}
</div>
